class GridLoadMore extends HTMLElement {
  constructor() {
    super();
    this.isLoading = false;
    this.grid = this.querySelector("[grid]");
    this.section = this.getAttribute("data-section");
    this.selectors = this.getAttribute("data-selectors");
    this.nextLink = this.getAttribute("data-next-url");
    this.loadMoreBtn = this.querySelector("[load-more-btn]");
    this._persistScrollTimer = null;
  }

  /** Keep scroll position for the same listing URL in sessionStorage (navigation back / reload + backfill). */
  _onPageHide = () => {
    this.persistScrollPosition();
  };

  _persistScrollOnScroll = () => {
    window.clearTimeout(this._persistScrollTimer);
    this._persistScrollTimer = window.setTimeout(() => this.persistScrollPosition(), 200);
  };

  _onGridLinkClick = (event) => {
    const anchor = event.target instanceof Element ? event.target.closest("a[href]") : null;
    if (!anchor || !this.grid?.contains(anchor)) return;
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (anchor.target && anchor.target.toLowerCase() !== "_self") return;
    if (anchor.hasAttribute("download")) return;

    this.persistNavigationIntent(anchor.href);
  };

  connectedCallback() {
    if (!this.grid) {
      console.error("GridLoadMore: No grid element found with [grid] attribute");
      return;
    }

    if (!this.loadMoreBtn) {
      return;
    }

    if (!this.selectors) {
      console.error("GridLoadMore: No selectors specified in data-selectors attribute");
      return;
    }

    this.loadMoreBtn.addEventListener("click", this.loadMoreClickHandler);
    window.addEventListener("pagehide", this._onPageHide);
    window.addEventListener("scroll", this._persistScrollOnScroll, { passive: true });
    this.grid.addEventListener("click", this._onGridLinkClick);

    /** Preserved so the spinner can be swapped back for the button's label after each load. */
    if (this._loadMoreDefaultInnerHtml == null) {
      this._loadMoreDefaultInnerHtml = this.loadMoreBtn.innerHTML;
    }

    // Defer restore until after load + frames so browser scroll restoration can run first.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        void this.initializeLoadState();
      });
    });
  }

  /**
   * After navigation, restored scroll often lands after `load` + paint; waiting avoids capturing `0` too early.
   */
  waitForStableScrollRestoration() {
    return new Promise((resolve) => {
      const done = () =>
        requestAnimationFrame(() => requestAnimationFrame(resolve));

      if (document.readyState === "complete") {
        done();
      } else {
        window.addEventListener("load", () => done(), { once: true });
      }
    });
  }

  getScrollRestoreKey() {
    return `${this.getCollectionKey()}_scrollY`;
  }

  getRestoreIntentKey() {
    return `${this.getCollectionKey()}_restoreIntent`;
  }

  persistNavigationIntent(targetHref) {
    try {
      const targetUrl = new URL(targetHref, window.location.href);
      sessionStorage.setItem(
        this.getRestoreIntentKey(),
        JSON.stringify({
          fromKey: this.getCollectionKey(),
          timestamp: Date.now(),
        })
      );
    } catch {
      /* ignore malformed URL / storage issues */
    }
  }

  consumeNavigationIntent() {
    try {
      const key = this.getRestoreIntentKey();
      const rawIntent = sessionStorage.getItem(key);
      sessionStorage.removeItem(key);
      if (!rawIntent) return false;

      const intent = JSON.parse(rawIntent);
      if (!intent || intent.fromKey !== this.getCollectionKey()) return false;

      // Keep intent short-lived so stale sessions do not unexpectedly restore.
      return Date.now() - Number(intent.timestamp || 0) < 30 * 60 * 1000;
    } catch {
      return false;
    }
  }

  isHistoryNavigation() {
    try {
      const navEntry = performance.getEntriesByType("navigation")?.[0];
      if (navEntry && typeof navEntry.type === "string") {
        return navEntry.type === "back_forward";
      }
    } catch {
      /* ignore */
    }

    // Fallback for older browsers.
    return performance.navigation?.type === 2;
  }

  persistScrollPosition() {
    try {
      const y = window.scrollY || document.documentElement.scrollTop || 0;
      sessionStorage.setItem(this.getScrollRestoreKey(), String(y));
    } catch {
      /* ignore quota / private mode */
    }
  }

  /**
   * Read the deepest plausible scroll target before backfill (live position + last saved for this listing).
   */
  getBackfillScrollTarget() {
    let y = window.scrollY || document.documentElement.scrollTop || 0;
    try {
      const raw = sessionStorage.getItem(this.getScrollRestoreKey());
      if (raw != null) {
        const saved = parseInt(raw, 10);
        if (Number.isFinite(saved)) {
          y = Math.max(y, saved);
        }
      }
    } catch {
      /* ignore */
    }
    return y;
  }

  /**
   * Apply scroll after DOM changes; repeat to beat late browser restoration and `scroll-behavior: smooth`.
   */
  clampAndRestoreScroll(targetY) {
    const html = document.documentElement;
    html.style.scrollBehavior = "auto";

    const apply = () => {
      const maxScroll = Math.max(0, html.scrollHeight - window.innerHeight);
      const top = Math.max(0, Math.min(targetY, maxScroll));
      window.scrollTo(0, top);
    };

    apply();
    requestAnimationFrame(() => requestAnimationFrame(apply));
    [0, 50, 120, 320, 600].forEach((ms) => window.setTimeout(apply, ms));

    window.setTimeout(() => {
      html.style.removeProperty("scroll-behavior");
    }, 650);
  }

  async initializeLoadState() {
    await this.waitForStableScrollRestoration();

    if (!this.isHistoryNavigation()) return;

    const hasBackNavigationIntent = this.consumeNavigationIntent();
    if (!hasBackNavigationIntent) return;

    const storedCount = this.getStoredLoadedCount();
    const currentCount = this.getCurrentProductCount();

    if (storedCount > currentCount && this.nextLink) {
      await this.backfillItemsIfNeeded();
    }
  }

  async backfillItemsIfNeeded() {
    try {
      const storedCount = this.getStoredLoadedCount();
      const currentCount = this.getCurrentProductCount();

      if (storedCount > currentCount && this.nextLink) {
        this.grid?.classList.add("filter-results-pending");

        // Prefer saved + live max: restoration and VT can report 0 until backfill finishes.
        const initialScrollY = this.getBackfillScrollTarget();

        // Fetch all needed pages and collect items in a single fragment
        const allItemsFragment = document.createDocumentFragment();
        let currentNextLink = this.nextLink;
        let itemsCollected = 0;
        const itemsNeeded = storedCount - currentCount;

        // Process pages until we have enough items
        while (currentNextLink && itemsCollected < itemsNeeded) {
          const pageResult = await this.fetchPageData(currentNextLink);

          if (!pageResult.success || !pageResult.items.length) {
            console.warn("Failed to fetch page or no items found:", currentNextLink);
            break;
          }

          // Add items to our fragment (only add what we need)
          for (const item of pageResult.items) {
            if (itemsCollected >= itemsNeeded) break;
            allItemsFragment.appendChild(item);
            itemsCollected++;
          }

          // Update next link for subsequent requests
          currentNextLink = pageResult.nextLink;
        }

        // Add all collected items at once
        if (allItemsFragment.children.length > 0) {
          this.grid.appendChild(allItemsFragment);


          // Update the nextLink for future load more operations
          this.nextLink = currentNextLink;

          // Hide load more button if no more pages
          if (!currentNextLink && this.loadMoreBtn) {
            this.loadMoreBtn.classList.add("hidden");
          }
        }

        this.grid?.classList.remove("filter-results-pending");
        this.clampAndRestoreScroll(initialScrollY);
        this.persistScrollPosition();
      } else {
        this.grid?.classList.remove("filter-results-pending");
      }
    } catch (error) {
      console.warn("Failed to backfill items:", error);
      this.grid?.classList.remove("filter-results-pending");
    }
  }

  async fetchPageData(url) {
    // Fetch page data without adding to DOM - used for backfilling
    try {
      const response = await fetch(url, { method: "GET" });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, "text/html");

      // Scope to THIS listing's grid (see fetchNextPage) so recommendation / other-section
      // product-cards on the page aren't collected.
      const nextGridElement =
        doc.querySelector(`grid-load-more[data-section="${this.section}"]`) ||
        doc.querySelector("grid-load-more");
      const scope = nextGridElement?.querySelector("[grid]") || nextGridElement || doc;
      const newItems = scope.querySelectorAll(this.selectors);

      // Get next URL for subsequent requests
      const nextLink = nextGridElement?.getAttribute("data-next-url") || null;

      return {
        success: true,
        items: Array.from(newItems), // Convert NodeList to Array for easier handling
        nextLink: nextLink,
      };
    } catch (error) {
      console.error("Failed to fetch page data:", error);
      return {
        success: false,
        items: [],
        nextLink: null,
      };
    }
  }

  getCurrentProductCount() {
    return this.grid.querySelectorAll("product-card").length;
  }

  getCollectionKey() {
    // Create a unique key for this collection page including filters
    const url = new URL(window.location.href);
    url.searchParams.delete("page"); // Remove pagination to get base collection
    return `loadmore_${btoa(url.pathname + url.search).replace(/[^a-zA-Z0-9]/g, "")}`;
  }

  storeLoadedCount() {
    try {
      const currentCount = this.getCurrentProductCount();
      const key = this.getCollectionKey();
      localStorage.setItem(key, currentCount.toString());
      this.persistScrollPosition();
    } catch (error) {
      console.warn("Failed to store loaded count:", error);
    }
  }

  getStoredLoadedCount() {
    try {
      const key = this.getCollectionKey();
      const stored = localStorage.getItem(key);
      return stored ? parseInt(stored, 10) : 0;
    } catch (error) {
      console.warn("Failed to get stored loaded count:", error);
      return 0;
    }
  }

  clearStoredCount() {
    try {
      const key = this.getCollectionKey();
      localStorage.removeItem(key);
      sessionStorage.removeItem(this.getScrollRestoreKey());
      sessionStorage.removeItem(this.getRestoreIntentKey());
    } catch (error) {
      console.warn("Failed to clear stored count:", error);
    }
  }

  loadMoreClickHandler = (event) => {
    event.preventDefault();
    if (this.loadMoreBtn.disabled || this.isLoading) return;

    // Clear any previous error state
    this.loadMoreBtn.classList.remove("error");

    this.loadMoreBtn.disabled = true;
    this.loadMoreBtn.classList.add("loading");
    this.loadMoreBtn.setAttribute("aria-busy", "true");
    this.lockLoadMoreButtonSize();

    const loader = document.createElement("span");
    loader.classList.add("grid-loader");
    loader.setAttribute("aria-hidden", "true");
    loader.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" focusable="false" aria-hidden="true">
        <path fill="var(--brand-color-1)" d="M43.935,25.145c0-10.318-8.364-18.683-18.683-18.683c-10.318,0-18.683,8.365-18.683,18.683h4.068c0-8.071,6.543-14.615,14.615-14.615c8.072,0,14.615,6.543,14.615,14.615H43.935z">
          <animateTransform attributeType="xml"
            attributeName="transform"
            type="rotate"
            from="0 25 25"
            to="360 25 25"
            dur="0.6s"
            repeatCount="indefinite"/>
        </path>
      </svg>
    `;
    this.loadMoreBtn.replaceChildren(loader);
    this.fetchNextPage();
  };

  lockLoadMoreButtonSize() {
    if (!this.loadMoreBtn) return;
    const rect = this.loadMoreBtn.getBoundingClientRect();
    const width = Math.round(rect.width);
    const height = Math.round(rect.height);
    if (width > 0) {
      this.loadMoreBtn.style.minWidth = `${width}px`;
      this.loadMoreBtn.style.width = `${width}px`;
      this.loadMoreBtn.style.maxWidth = `${width}px`;
    }
    if (height > 0) {
      this.loadMoreBtn.style.minHeight = `${height}px`;
      this.loadMoreBtn.style.height = `${height}px`;
      this.loadMoreBtn.style.maxHeight = `${height}px`;
    }
  }

  unlockLoadMoreButtonSize() {
    if (!this.loadMoreBtn) return;
    this.loadMoreBtn.style.removeProperty("min-width");
    this.loadMoreBtn.style.removeProperty("width");
    this.loadMoreBtn.style.removeProperty("max-width");
    this.loadMoreBtn.style.removeProperty("min-height");
    this.loadMoreBtn.style.removeProperty("height");
    this.loadMoreBtn.style.removeProperty("max-height");
  }

  /** Put the just-loaded page number in the address bar without navigating. */
  syncUrlToLoadedPage(loadedUrl) {
    try {
      const loaded = new URL(loadedUrl, window.location.href);
      const page = loaded.searchParams.get("page");
      if (!page) return;
      const current = new URL(window.location.href);
      current.searchParams.set("page", page);
      window.history.replaceState(window.history.state, "", current.toString());
    } catch {
      /* malformed URL — the listing still works, only the address bar lags */
    }
  }

  /**
   * Keep the visually-hidden pagination anchors truthful after an AJAX load. They're what a crawler
   * (or anyone tabbing through) follows, so they have to describe the CURRENT position, not the
   * server-rendered one. They live beside the button rather than inside it, so the button's
   * innerHTML restore in fetchNextPage() leaves them alone.
   */
  syncPaginationLinks(loadedUrl, nextUrl) {
    const container = this.querySelector(".pagination-links");
    if (!container) return;

    const prev = container.querySelector("[prev-link]");
    const next = container.querySelector("[next-link]");
    if (prev) prev.setAttribute("href", loadedUrl);
    if (next) {
      if (nextUrl) {
        next.setAttribute("href", nextUrl);
      } else {
        next.remove();
      }
    }
  }

  async fetchNextPage() {
    // Prevent multiple concurrent requests
    if (this.isLoading) {
      return;
    }

    if (!this.nextLink) {
      console.warn("GridLoadMore: No next link found");
      this.loadMoreBtn.disabled = false;
      this.loadMoreBtn.classList.remove("loading");
      this.loadMoreBtn.removeAttribute("aria-busy");
      this.unlockLoadMoreButtonSize();
      this.loadMoreBtn.classList.add("hidden");
      this.loadMoreBtn.innerHTML = this._loadMoreDefaultInnerHtml || this.loadMoreBtn.innerHTML;
      return;
    }

    this.isLoading = true;
    const nextUrl = this.nextLink;
    try {
      const response = await fetch(nextUrl, { method: "GET" });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, "text/html");

      // Scope to THIS listing's grid so we don't scoop up product-cards from other sections
      // on the page (e.g. cart recommendations, which server-render their own product-cards).
      const nextGridElement =
        doc.querySelector(`grid-load-more[data-section="${this.section}"]`) ||
        doc.querySelector("grid-load-more");
      const scope = nextGridElement?.querySelector("[grid]") || nextGridElement || doc;
      const newItems = scope.querySelectorAll(this.selectors);

      // Safely get next URL
      const upComingNextURL = nextGridElement?.getAttribute("data-next-url") || null;
      if (!upComingNextURL) {
        this.loadMoreBtn.classList.add("hidden");
      }

      // Use DocumentFragment for efficient DOM manipulation
      const fragment = document.createDocumentFragment();
      newItems.forEach((item) => fragment.appendChild(item));
      this.grid.appendChild(fragment);


      // Store current loaded count in localStorage to preserve state on browser navigation
      this.storeLoadedCount();

      // Reflect the page now on screen in the address bar (…/collections/x?page=2), so the URL is
      // shareable and a copy-paste lands somewhere sensible. `replaceState`, not `pushState`: one
      // history entry per listing keeps Back going to whatever preceded the listing, which is what
      // the scroll/backfill restore in initializeLoadState() expects.
      this.syncUrlToLoadedPage(nextUrl);

      // The button's hidden prev/next anchors come from the server render, so after an AJAX load
      // they'd still advertise page 2. Re-point them at the real next page.
      this.syncPaginationLinks(nextUrl, upComingNextURL);

      this.nextLink = upComingNextURL;
    } catch (error) {
      console.error("GridLoadMore: Error fetching next page", error);

      // Show user-friendly error message
      if (this.loadMoreBtn) {
        this.loadMoreBtn.textContent = "Error loading more items. Click to retry.";
        this.loadMoreBtn.classList.add("error");
      }
    } finally {
      this.isLoading = false;
      if (this.loadMoreBtn) {
        this.loadMoreBtn.disabled = false;
        this.loadMoreBtn.classList.remove("loading");
        this.loadMoreBtn.removeAttribute("aria-busy");
        this.unlockLoadMoreButtonSize();
        if (!this.loadMoreBtn.classList.contains("error")) {
          this.loadMoreBtn.innerHTML = this._loadMoreDefaultInnerHtml || this.loadMoreBtn.innerHTML;
        }
      }
    }
  }


  disconnectedCallback() {
    window.removeEventListener("pagehide", this._onPageHide);
    window.removeEventListener("scroll", this._persistScrollOnScroll);
    window.clearTimeout(this._persistScrollTimer);
    this.grid?.removeEventListener("click", this._onGridLinkClick);

    if (this.loadMoreBtn) {
      this.loadMoreBtn.removeEventListener("click", this.loadMoreClickHandler);
    }
  }
}

customElements.define("grid-load-more", GridLoadMore);
